import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import type { RunTarget } from '../../services/execution-job.service';
import { RunnerPairingService, type PairedRunner } from '../../services/runner-pairing.service';
import { labelOptions, preselect } from '../runner-target-select/runner-options';

export type RunnerAvailability = 'none' | 'allOffline' | 'someOnline';

type LoadState = 'loading' | 'loaded' | 'failed';

const LABEL_PREFIX = 'label:';

let nextId = 0;

@Component({
  selector: 'app-runner-radio-list',
  imports: [MatButton, MatIcon, MatProgressSpinner, TranslatePipe],
  templateUrl: './runner-radio-list.component.html',
  styleUrl: './runner-radio-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunnerRadioListComponent implements OnInit {
  private readonly runnerPairing = inject(RunnerPairingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly projectId = input.required<string>();
  /** Name eines gerade verbundenen Rechners: gewaehlt, sobald er online in der Liste steht. */
  readonly selectWhenOnline = input<string | null>(null);

  readonly targetChange = output<RunTarget | null>();
  readonly availabilityChange = output<RunnerAvailability>();

  readonly headingId = `runner-radio-list-heading-${nextId++}`;
  readonly labelPrefix = LABEL_PREFIX;

  readonly loadState = signal<LoadState>('loading');
  readonly runners = signal<PairedRunner[]>([]);
  readonly selectedValue = signal<string | null>(null);

  readonly labels = computed(() => labelOptions(this.runners()));
  readonly allOffline = computed(() => this.runners().length > 0 && !this.runners().some((runner) => runner.online));

  /** Reihenfolge der Pfeiltasten: erst die Rechner, dann die Anforderungen. */
  private readonly selectableValues = computed(() => [
    ...this.runners()
      .filter((runner) => runner.online)
      .map((runner) => runner.id),
    ...this.labels().map((label) => LABEL_PREFIX + label),
  ]);

  /** Roving-Tabindex: die Wahl, ohne Wahl der erste waehlbare Eintrag. */
  readonly tabStop = computed(() => this.selectedValue() ?? this.selectableValues()[0] ?? null);

  private readonly target = computed<RunTarget | null>(() => {
    const value = this.selectedValue();
    if (value === null || this.loadState() !== 'loaded') {
      return null;
    }
    if (value.startsWith(LABEL_PREFIX)) {
      return { kind: 'labels', labels: [value.slice(LABEL_PREFIX.length)] };
    }
    const runner = this.runners().find((each) => each.id === value);
    return runner ? { kind: 'runner', runnerId: runner.id, runnerName: runner.name } : null;
  });

  private loading?: Subscription;

  constructor() {
    effect(() => {
      const name = this.selectWhenOnline();
      untracked(() => {
        if (name !== null && this.loadState() === 'loaded' && this.selectArrived(name)) {
          this.targetChange.emit(this.target());
        }
      });
    });
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading?.unsubscribe();
    this.loadState.set('loading');
    this.targetChange.emit(null);
    this.loading = this.runnerPairing
      .listRunners(false, this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (runners) => this.applyLoaded(runners),
        error: () => this.loadState.set('failed'),
      });
  }

  isSelected(value: string): boolean {
    return this.selectedValue() === value;
  }

  choose(value: string): void {
    if (!this.selectableValues().includes(value)) {
      return;
    }
    this.selectedValue.set(value);
    this.targetChange.emit(this.target());
  }

  onKeydown(event: KeyboardEvent, value: string): void {
    const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.choose(value);
      return;
    }
    if (step === undefined) {
      return;
    }
    event.preventDefault();
    const values = this.selectableValues();
    if (values.length === 0) {
      return;
    }
    const index = values.indexOf(value);
    const next = values[(index + step + values.length) % values.length];
    this.choose(next);
    this.focusOption(next);
  }

  private applyLoaded(runners: PairedRunner[]): void {
    const previous = this.selectedValue();
    this.runners.set(runners);
    this.loadState.set('loaded');
    const name = this.selectWhenOnline();
    if (name === null || !this.selectArrived(name)) {
      const keep = previous !== null && this.selectableValues().includes(previous);
      this.selectedValue.set(keep ? previous : preselect(runners));
    }
    this.availabilityChange.emit(this.availability(runners));
    this.targetChange.emit(this.target());
  }

  private selectArrived(name: string): boolean {
    const arrived = this.runners().find((runner) => runner.name === name && runner.online);
    if (arrived) {
      this.selectedValue.set(arrived.id);
    }
    return arrived !== undefined;
  }

  private availability(runners: readonly PairedRunner[]): RunnerAvailability {
    if (runners.length === 0) {
      return 'none';
    }
    return runners.some((runner) => runner.online) ? 'someOnline' : 'allOffline';
  }

  private focusOption(value: string): void {
    this.host.nativeElement.querySelector<HTMLElement>(`[data-value="${CSS.escape(value)}"]`)?.focus();
  }
}
